import { useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  ArrowLeft,
  Sprout,
  AlertTriangle,
  CheckCircle2,
  RefreshCw,
  Globe,
  Building2,
  Plane,
  Hand,
} from 'lucide-react'
import {
  getLantanaCoverageReport,
  createLantanaSnapshot,
  getAIModels,
} from '../../services/api'
import { useAuth } from '../../contexts/AuthContext'
import { isSystemAdmin, Role } from '../../lib/permissions'

const SOURCE_OPTIONS = [
  { value: '',          label: 'All sources' },
  { value: 'drone',     label: 'Drone' },
  { value: 'satellite', label: 'Satellite' },
  { value: 'manual',    label: 'Manual' },
]

const SOURCE_ICON = {
  drone:     Plane,
  satellite: Globe,
  manual:    Hand,
}

function todayIso() {
  return new Date().toISOString().slice(0, 10)
}

// CAL §3.4(b) baseline ≥2 ha; §3.4(f) cleared ≥1 ha; biochar_applied no
// hard target — gauge displays cumulative ha.
function MilestoneGauge({ kind, areaHa, count, targetHa }) {
  const labels = {
    lantana_baseline: 'Lantana baseline',
    cleared:          'Cleared',
    biochar_applied:  'Biochar applied',
  }
  const cite = {
    lantana_baseline: 'CAL §3.4(b)',
    cleared:          'CAL §3.4(f)',
    biochar_applied:  'CAL §3.4(f)/(h)',
  }
  const pct = targetHa
    ? Math.min(100, Math.round((areaHa / targetHa) * 100))
    : null
  const reached = targetHa != null && areaHa >= targetHa
  return (
    <div className="card p-5 flex flex-col justify-between">
      <div>
        <div className="flex items-center justify-between">
          <div className="text-sm font-medium text-gray-600">
            {labels[kind] || kind}
          </div>
          {reached && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
              <CheckCircle2 className="h-3 w-3" />
              target reached
            </span>
          )}
        </div>
        <div className="text-xs text-gray-400 mt-0.5">{cite[kind]}</div>
      </div>
      <div className="mt-3">
        <div className="text-3xl font-bold text-gray-900">
          {areaHa.toFixed(2)} <span className="text-base font-normal text-gray-500">ha</span>
        </div>
        <div className="text-xs text-gray-500">
          {count} claim{count === 1 ? '' : 's'} on the ledger
          {targetHa != null && (
            <> • target {targetHa.toFixed(1)} ha</>
          )}
        </div>
        {targetHa != null && (
          <div className="mt-2 h-2 w-full bg-gray-200 rounded-full overflow-hidden">
            <div
              className={`h-full ${reached ? 'bg-green-500' : 'bg-amber-500'} transition-all`}
              style={{ width: `${pct ?? 0}%` }}
            />
          </div>
        )}
      </div>
    </div>
  )
}

function SourceBadge({ source }) {
  if (!source) return null
  const Icon = SOURCE_ICON[source] || Sprout
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
      <Icon className="h-3 w-3" />
      {source}
    </span>
  )
}

function CoverageBar({ pct }) {
  // Lantana % is "% of paddock taken over by lantana" — higher is worse,
  // so flip the colour scale relative to CycleProgress's "% of target hit".
  const safePct = Math.max(0, Math.min(100, pct ?? 0))
  const color =
    safePct >= 25 ? 'bg-red-500'
    : safePct >= 10 ? 'bg-amber-500'
    : 'bg-green-500'
  return (
    <div className="flex items-center gap-2 min-w-[120px]">
      <div className="h-2 flex-1 bg-gray-200 rounded-full overflow-hidden">
        <div className={`h-full ${color} transition-all`} style={{ width: `${safePct}%` }} />
      </div>
      <span className="text-xs text-gray-600 tabular-nums w-10 text-right">
        {safePct.toFixed(1)}%
      </span>
    </div>
  )
}

export default function LantanaCoverageReport({ ranchId }) {
  const { role } = useAuth()
  const canSnapshot = role === Role.LAND_MONITOR || isSystemAdmin(role)
  const [onDate, setOnDate] = useState(todayIso())
  const [source, setSource] = useState('')
  const [snapshotForm, setSnapshotForm] = useState({
    source: 'drone',
    snapshot_date: todayIso(),
    model_run_id: '',
  })
  const [snapshotError, setSnapshotError] = useState(null)
  const [snapshotSuccess, setSnapshotSuccess] = useState(null)

  const queryClient = useQueryClient()

  const { data, isLoading, error } = useQuery({
    queryKey: ['lantanaCoverageReport', ranchId, onDate, source || null],
    queryFn: () => getLantanaCoverageReport({
      ranch_id: ranchId,
      on_date: onDate,
      source: source || undefined,
    }),
    enabled: Boolean(ranchId),
  })

  // Active models for the snapshot form's model_run_id picker. Fail-soft: if
  // /ai-models 401s for a non-sysadmin viewer, the picker still renders empty.
  const { data: models } = useQuery({
    queryKey: ['aiModels', { is_active: true }],
    queryFn: () => getAIModels({ is_active: true }),
  })
  const lantanaModels = useMemo(
    () => (models || []).filter(m =>
      m.model_kind === 'lantana_detector' ||
      m.model_kind === 'vegetation_health_classifier' ||
      m.model_kind === 'paddock_segmenter'
    ),
    [models],
  )

  const snapshotMutation = useMutation({
    mutationFn: () => createLantanaSnapshot({
      ranch_id: ranchId,
      snapshot_date: snapshotForm.snapshot_date,
      source: snapshotForm.source,
      model_run_id: snapshotForm.model_run_id || null,
    }),
    onSuccess: (out) => {
      setSnapshotError(null)
      setSnapshotSuccess(
        `Snapshot saved — ${out.paddock_count} paddock${out.paddock_count === 1 ? '' : 's'} ` +
        `+ ranch aggregate (${out.ranch_lantana_ha.toFixed(2)} / ${out.ranch_total_ha.toFixed(2)} ha lantana)`
      )
      queryClient.invalidateQueries({ queryKey: ['lantanaCoverageReport', ranchId] })
    },
    onError: (err) => {
      setSnapshotSuccess(null)
      const detail = err?.response?.data?.detail
      setSnapshotError(typeof detail === 'string' ? detail : err?.message || 'Snapshot failed')
    },
  })

  const aggregate = data?.aggregate
  const paddocks = data?.paddocks || []
  const cal = data?.cal_milestones || {
    lantana_baseline: { area_ha: 0, count: 0, target_ha: 2.0 },
    cleared:          { area_ha: 0, count: 0, target_ha: 1.0 },
    biochar_applied:  { area_ha: 0, count: 0, target_ha: null },
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Link to="/land" className="p-2 hover:bg-gray-100 rounded-lg">
          <ArrowLeft className="h-5 w-5 text-gray-500" />
        </Link>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">Lantana Coverage Report</h1>
          <p className="text-gray-500 mt-1">
            Per-paddock lantana density + CAL milestone gauges from the spatial uniqueness ledger.
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="card p-4 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="label" htmlFor="lcr-on-date">As of date</label>
          <input
            id="lcr-on-date"
            type="date"
            className="input w-full"
            value={onDate}
            onChange={e => setOnDate(e.target.value)}
            max={todayIso()}
          />
        </div>
        <div>
          <label className="label" htmlFor="lcr-source">Source</label>
          <select
            id="lcr-source"
            className="input w-full"
            value={source}
            onChange={e => setSource(e.target.value)}
          >
            {SOURCE_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
        <div className="flex items-end gap-2 text-xs text-gray-500">
          {data?.aggregate?.snapshot_date && (
            <div className="flex items-center gap-1">
              <SourceBadge source={data.aggregate.source} />
              <span>last snapshot {data.aggregate.snapshot_date}</span>
            </div>
          )}
        </div>
      </div>

      {/* CAL gauges */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <MilestoneGauge
          kind="lantana_baseline"
          areaHa={cal.lantana_baseline?.area_ha ?? 0}
          count={cal.lantana_baseline?.count ?? 0}
          targetHa={cal.lantana_baseline?.target_ha ?? 2.0}
        />
        <MilestoneGauge
          kind="cleared"
          areaHa={cal.cleared?.area_ha ?? 0}
          count={cal.cleared?.count ?? 0}
          targetHa={cal.cleared?.target_ha ?? 1.0}
        />
        <MilestoneGauge
          kind="biochar_applied"
          areaHa={cal.biochar_applied?.area_ha ?? 0}
          count={cal.biochar_applied?.count ?? 0}
          targetHa={cal.biochar_applied?.target_ha ?? null}
        />
      </div>

      {/* Generate snapshot */}
      {canSnapshot && (
        <div className="card p-4 space-y-3">
          <div className="flex items-center gap-2">
            <RefreshCw className="h-4 w-4 text-gray-500" />
            <h2 className="font-medium text-gray-900">Generate snapshot</h2>
          </div>
          <p className="text-xs text-gray-500">
            Re-runs replace the prior snapshot for the same (date, source, model);
            the old row is soft-archived for audit.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <label className="label">Date</label>
              <input
                type="date"
                className="input w-full"
                value={snapshotForm.snapshot_date}
                onChange={e => setSnapshotForm(f => ({ ...f, snapshot_date: e.target.value }))}
                max={todayIso()}
              />
            </div>
            <div>
              <label className="label">Source</label>
              <select
                className="input w-full"
                value={snapshotForm.source}
                onChange={e => setSnapshotForm(f => ({ ...f, source: e.target.value }))}
              >
                <option value="drone">Drone</option>
                <option value="satellite">Satellite</option>
                <option value="manual">Manual</option>
              </select>
            </div>
            <div>
              <label className="label">Model (optional)</label>
              <select
                className="input w-full"
                value={snapshotForm.model_run_id}
                onChange={e => setSnapshotForm(f => ({ ...f, model_run_id: e.target.value }))}
              >
                <option value="">— none —</option>
                {lantanaModels.map(m => (
                  <option key={m.id} value={m.id}>
                    {m.model_kind} v{m.version}{m.scope === 'global' ? ' • global' : ' • per-ranch'}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <button
                type="button"
                className="btn btn-primary w-full disabled:opacity-50"
                disabled={snapshotMutation.isPending}
                onClick={() => snapshotMutation.mutate()}
              >
                {snapshotMutation.isPending ? 'Saving…' : 'Generate'}
              </button>
            </div>
          </div>
          {snapshotError && (
            <div className="text-sm text-red-700 bg-red-50 p-2 rounded">
              {snapshotError}
            </div>
          )}
          {snapshotSuccess && (
            <div className="text-sm text-green-700 bg-green-50 p-2 rounded">
              {snapshotSuccess}
            </div>
          )}
        </div>
      )}

      {/* Loading / error */}
      {isLoading && (
        <div className="card p-8 text-center text-gray-400">Loading…</div>
      )}
      {error && (
        <div className="card p-6 flex items-center gap-2 text-red-700 bg-red-50">
          <AlertTriangle className="h-5 w-5" />
          {error.response?.data?.detail || 'Failed to load coverage report.'}
        </div>
      )}

      {/* Aggregate + per-paddock table */}
      {data && (
        <div className="card overflow-hidden">
          <div className="p-4 border-b bg-gray-50 flex items-center justify-between">
            <div>
              <div className="text-sm text-gray-500">Ranch aggregate</div>
              <div className="text-xl font-bold text-gray-900 mt-0.5">
                {(aggregate?.lantana_ha ?? 0).toFixed(2)} ha lantana
                <span className="text-gray-500 font-normal">
                  {' '}/ {(aggregate?.total_ha ?? 0).toFixed(2)} ha total
                </span>
              </div>
              <div className="text-xs text-gray-500 mt-0.5">
                {aggregate
                  ? `${aggregate.coverage_pct.toFixed(2)}% coverage • ${aggregate.polygon_count} polygons`
                  : 'No snapshot yet for this date / source'}
              </div>
            </div>
            {aggregate && <SourceBadge source={aggregate.source} />}
          </div>

          {paddocks.length === 0 ? (
            <div className="p-10 text-center text-gray-400 flex flex-col items-center gap-2">
              <Sprout className="h-8 w-8 text-gray-300" />
              <div>No paddock snapshots yet for this filter.</div>
              {canSnapshot && (
                <div className="text-xs">Use the form above to generate one.</div>
              )}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 uppercase text-xs">
                <tr>
                  <th className="px-4 py-2 text-left">Paddock</th>
                  <th className="px-4 py-2 text-right">Total ha</th>
                  <th className="px-4 py-2 text-right">Lantana ha</th>
                  <th className="px-4 py-2 text-left">Coverage %</th>
                  <th className="px-4 py-2 text-left">Source</th>
                  <th className="px-4 py-2 text-left">Snapshot date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {paddocks.map(p => (
                  <tr key={p.snapshot_id} className="hover:bg-gray-50">
                    <td className="px-4 py-2">
                      <div className="font-medium text-gray-900">{p.paddock_code}</div>
                      <div className="text-xs text-gray-500">{p.paddock_name}</div>
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums">{p.total_ha.toFixed(2)}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{p.lantana_ha.toFixed(2)}</td>
                    <td className="px-4 py-2"><CoverageBar pct={p.coverage_pct} /></td>
                    <td className="px-4 py-2"><SourceBadge source={p.source} /></td>
                    <td className="px-4 py-2 text-xs text-gray-500">{p.snapshot_date}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Sysadmin signal: model registry shortcut */}
      {isSystemAdmin(role) && (
        <div className="text-xs text-gray-400 flex items-center gap-1">
          <Building2 className="h-3 w-3" />
          Model registry:{' '}
          <Link to="/ai-models" className="underline hover:text-gray-600">
            /ai-models
          </Link>
        </div>
      )}
    </div>
  )
}
